import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiEdit, FiTrash2, FiX } from "react-icons/fi";

function FieldLabel({ children, required = false }) {
  return (
    <span className="stock-inv-field-name">
      {children}
      {required ? <span className="stock-inv-required">*</span> : null}
    </span>
  );
}

function useModalLock(isOpen, onClose) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);
}

export function ExtraColumnHeader({ columnKey, name, onRename, onDelete }) {
  return (
    <span className="stock-inv-col-head">
      <span>{name}</span>
      <span className="stock-inv-col-head-actions">
        <button
          type="button"
          className="stock-inv-icon-btn"
          title={`Rename "${name}"`}
          aria-label={`Rename ${name}`}
          onClick={(event) => {
            event.stopPropagation();
            onRename(columnKey || name);
          }}
        >
          <FiEdit />
        </button>
        <button
          type="button"
          className="stock-inv-icon-btn stock-inv-icon-btn-danger"
          title={`Delete "${name}"`}
          aria-label={`Delete ${name}`}
          onClick={(event) => {
            event.stopPropagation();
            onDelete(columnKey || name);
          }}
        >
          <FiTrash2 />
        </button>
      </span>
    </span>
  );
}

export function RenameFieldModal({
  isOpen,
  fields = [],
  extraKeys,
  initialFrom = "",
  existingKeys,
  overlayClassName = "",
  onClose,
  onRename,
  isSaving,
  error,
}) {
  const options = fields.length
    ? fields
    : (extraKeys || []).map((key) => ({ key, label: key }));
  const [fromKey, setFromKey] = useState("");
  const [toKey, setToKey] = useState("");
  const [localError, setLocalError] = useState("");

  useModalLock(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;
    const start =
      options.find((field) => field.key === initialFrom || field.label === initialFrom) || options[0];
    setFromKey(start?.key || "");
    setToKey(start?.label || "");
    setLocalError("");
  }, [isOpen, initialFrom, fields, extraKeys]);

  if (!isOpen) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const from = fromKey.trim();
    const to = toKey.trim();
    const current = options.find((field) => field.key === from);
    if (!from) {
      setLocalError("Select a field to rename.");
      return;
    }
    if (!to) {
      setLocalError("Enter a new field name.");
      return;
    }
    const taken = (existingKeys || []).some((key) => {
      const lower = String(key || "").toLowerCase();
      return lower === to.toLowerCase() && lower !== String(current?.label || from).toLowerCase() && lower !== from.toLowerCase();
    });
    if (taken) {
      setLocalError("That field already exists.");
      return;
    }
    setLocalError("");
    onRename(from, to);
  };

  return createPortal(
    <div
      className={`stock-inv-modal-overlay ${overlayClassName}`.trim()}
      onClick={onClose}
      role="presentation"
    >
      <div className="stock-inv-modal stock-inv-modal-sm" onClick={(event) => event.stopPropagation()}>
        <div className="stock-inv-modal-header">
          <div>
            <h3>Rename field</h3>
            <p>This updates the column name on the table and in the Excel download.</p>
          </div>
          <button type="button" className="stock-inv-icon-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>
        <form className="stock-inv-form" onSubmit={handleSubmit}>
          <div className="stock-inv-modal-body">
            {options.length > 1 ? (
              <label>
                <FieldLabel required>Current field</FieldLabel>
                <select
                  value={fromKey}
                  onChange={(e) => {
                    const next = e.target.value;
                    setFromKey(next);
                    const match = options.find((field) => field.key === next);
                    if (match) setToKey(match.label);
                  }}
                  required
                >
                  {options.map((field) => (
                    <option key={field.key} value={field.key}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label>
                <FieldLabel>Current field</FieldLabel>
                <input value={options[0]?.label || fromKey} readOnly />
              </label>
            )}
            <label>
              <FieldLabel required>New field name</FieldLabel>
              <input
                value={toKey}
                onChange={(e) => setToKey(e.target.value)}
                placeholder="e.g. SKU"
                autoFocus
              />
            </label>
            {localError || error ? <div className="stock-inv-form-error">{localError || error}</div> : null}
          </div>
          <div className="stock-inv-modal-actions">
            <button type="button" className="stock-inv-btn stock-inv-btn-ghost" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button type="submit" className="stock-inv-btn stock-inv-btn-primary" disabled={isSaving || options.length === 0}>
              {isSaving ? "Saving..." : "Rename field"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export function DeleteFieldModal({
  isOpen,
  fields = [],
  extraKeys,
  initialKey = "",
  overlayClassName = "",
  onClose,
  onDelete,
  isSaving,
  error,
}) {
  const options = fields.length
    ? fields
    : (extraKeys || []).map((key) => ({ key, label: key }));
  const [fieldKey, setFieldKey] = useState("");

  useModalLock(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;
    const start =
      options.find((field) => field.key === initialKey || field.label === initialKey) || options[0];
    setFieldKey(start?.key || "");
  }, [isOpen, initialKey, fields, extraKeys]);

  if (!isOpen) return null;

  const selected = options.find((field) => field.key === fieldKey);

  const handleSubmit = (event) => {
    event.preventDefault();
    const key = fieldKey.trim();
    if (!key) return;
    onDelete(key);
  };

  return createPortal(
    <div
      className={`stock-inv-modal-overlay ${overlayClassName}`.trim()}
      onClick={onClose}
      role="presentation"
    >
      <div className="stock-inv-modal stock-inv-modal-sm" onClick={(event) => event.stopPropagation()}>
        <div className="stock-inv-modal-header">
          <div>
            <h3>Delete field</h3>
            <p>
              {selected?.extra
                ? "This removes the extra column and its values from every row."
                : "This hides the column from the table and Excel download. Existing values stay in the catalog."}
            </p>
          </div>
          <button type="button" className="stock-inv-icon-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>
        <form className="stock-inv-form" onSubmit={handleSubmit}>
          <div className="stock-inv-modal-body">
            <label>
              <FieldLabel required>Field to delete</FieldLabel>
              <select value={fieldKey} onChange={(e) => setFieldKey(e.target.value)} required>
                {options.map((field) => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                  </option>
                ))}
              </select>
            </label>
            {error ? <div className="stock-inv-form-error">{error}</div> : null}
          </div>
          <div className="stock-inv-modal-actions">
            <button type="button" className="stock-inv-btn stock-inv-btn-ghost" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              type="submit"
              className="stock-inv-btn stock-inv-btn-danger"
              disabled={isSaving || options.length === 0}
            >
              {isSaving ? "Deleting..." : "Delete field"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
